import { ONE, ZERO, add, div, mul, sub, type Num } from './num.js';
import type { Content } from './content.js';
import { checkMilestones } from './derive.js';
import { resourcePassiveRatePerSec } from './selectors.js';
import { getResource, type GameState } from './state.js';

/** Logic ticks per second — the fixed 100 ms timestep (tech-design §2.3). */
const TICKS_PER_SEC = 10;

/**
 * Advance the simulation by a whole number of fixed logic ticks (tech-design §2.3).
 *
 * Pure and deterministic: the same `state` + `content` + `dtTicks` always yields
 * the same output. Production is linear within the call (the chunked-offline path
 * in GAM-7 slices a long absence into ~1000 of these so mid-absence unlocks still
 * fire); the exact same code path serves the live loop, background catch-up and
 * the headless pacing bot.
 *
 * Converters run only as far as their inputs allow — consumption is capped at the
 * available stock so no resource is ever driven negative (§2.2).
 */
export function tick(state: GameState, content: Content, dtTicks: number): GameState {
  if (!Number.isInteger(dtTicks) || dtTicks < 0) {
    throw new RangeError(
      `dtTicks must be a non-negative integer, got ${String(dtTicks)}`,
    );
  }
  if (dtTicks === 0) return state;

  const dtSec = div(dtTicks, TICKS_PER_SEC);
  const produced = new Map<string, Num>();
  const consumed = new Map<string, Num>();
  const accumulate = (map: Map<string, Num>, id: string, value: Num): void => {
    map.set(id, add(map.get(id) ?? ZERO, value));
  };

  // Passive production (effects on resource.<id>.passive).
  for (const [resourceId] of content.resources) {
    const rate = resourcePassiveRatePerSec(state, content, resourceId);
    if (rate.gt(ZERO)) accumulate(produced, resourceId, mul(rate, dtSec));
  }

  // Generator & converter throughput.
  for (const [genId, gen] of Object.entries(state.generators)) {
    if (gen.owned <= 0) continue;
    const def = content.generators.get(genId);
    if (def === undefined) continue;
    const throughput = mul(gen.multiplier, gen.owned);

    if (def.consumes !== undefined && def.consumes.length > 0) {
      // Fraction of the batch the inputs can sustain (0 halts, 1 runs fully).
      let fraction = ONE;
      for (const input of def.consumes) {
        const needed = mul(mul(input.baseRatePerSec, throughput), dtSec);
        if (needed.lte(ZERO)) continue;
        const feasible = div(getResource(state, input.resource).amount, needed);
        if (feasible.lt(fraction)) fraction = feasible;
      }
      if (fraction.lte(ZERO)) continue;
      if (fraction.gt(ONE)) fraction = ONE;
      const scaled = mul(dtSec, fraction);
      for (const input of def.consumes) {
        accumulate(
          consumed,
          input.resource,
          mul(mul(input.baseRatePerSec, throughput), scaled),
        );
      }
      for (const output of def.produces) {
        accumulate(
          produced,
          output.resource,
          mul(mul(output.baseRatePerSec, throughput), scaled),
        );
      }
    } else {
      for (const output of def.produces) {
        accumulate(
          produced,
          output.resource,
          mul(mul(output.baseRatePerSec, throughput), dtSec),
        );
      }
    }
  }

  // Apply net deltas: lifetime rises only with production; amount never negative.
  const resources: Record<string, GameState['resources'][string]> = {
    ...state.resources,
  };
  const touched = new Set<string>([...produced.keys(), ...consumed.keys()]);
  for (const id of touched) {
    const current = getResource(state, id);
    const gain = produced.get(id) ?? ZERO;
    const loss = consumed.get(id) ?? ZERO;
    const amount = sub(add(current.amount, gain), loss);
    resources[id] = {
      amount: amount.lt(ZERO) ? ZERO : amount,
      lifetime: add(current.lifetime, gain),
    };
  }

  const advanced: GameState = {
    ...state,
    resources,
    meta: { ...state.meta, ticks: state.meta.ticks + dtTicks },
  };

  // Threshold crossings (milestones) are honored to this call's resolution.
  return checkMilestones(advanced, content).state;
}
